import { Component, OnInit } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  CreditLimitsOverview,
  CreditLimitsService,
  LimitArea,
  UserLimitRow,
} from '../../services/credit-limits.service';

/**
 * Monatskontingent in Credits je interner Nutzer und Bereich.
 *
 * Leer = unbegrenzt, 0 = gesperrt. Ein eigenes Limit geht vor dem Standard;
 * ein Feld leeren nimmt das eigene Limit zurück (dann gilt wieder der
 * Standard). Gespeichert wird je Feld beim Verlassen bzw. mit Enter — ohne
 * eigenen Speichern-Knopf, damit eine Tabelle mit vielen Nutzern nicht zu
 * einem Formular wird, das man vergessen kann abzuschicken.
 */
@Component({
  selector: 'app-credit-limits-panel',
  templateUrl: './credit-limits-panel.component.html',
  styleUrl: './credit-limits-panel.component.scss',
})
export class CreditLimitsPanelComponent implements OnInit {
  overview: CreditLimitsOverview | null = null;
  /** Kopie fuer die Tabelle — einmal je Laden gebaut, nie im Getter
   *  (CLAUDE.md Regel 18). */
  rows: UserLimitRow[] = [];
  loading = false;
  loadFailed = false;
  saving: Record<string, boolean> = {};

  constructor(
    private limits: CreditLimitsService,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.limits.get().subscribe({
      next: (o) => this.apply(o),
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  private apply(o: CreditLimitsOverview): void {
    this.overview = o;
    this.rows = o.users;
    this.loading = false;
  }

  credits(milli: number): number {
    return (milli ?? 0) / 1000;
  }

  /** Anteil fuer den Balken; ohne Limit kein Balken. */
  pct(state: { limit_credits: number | null; used_milli: number }): number | null {
    if (state.limit_credits === null) return null;
    if (state.limit_credits === 0) return 100;
    return Math.min(100, (state.used_milli / 1000 / state.limit_credits) * 100);
  }

  /** Leeres Feld -> null; sonst ganze Zahl >= 0. Ungueltiges wird verworfen. */
  private parse(raw: string): number | null | undefined {
    const v = (raw ?? '').toString().trim();
    if (v === '') return null;
    const n = Number(v.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return undefined;
    return Math.round(n);
  }

  saveDefault(area: LimitArea, raw: string): void {
    const value = this.parse(raw);
    if (value === undefined || !this.overview || value === this.overview.defaults[area]) return;
    this.save(null, area, value);
  }

  saveUser(row: UserLimitRow, area: LimitArea, raw: string): void {
    const value = this.parse(raw);
    if (value === undefined) return;
    const state = row.areas[area];
    const current = state.own_limit ? state.limit_credits : null;
    if (value === current) return;
    this.save(row.user_id, area, value);
  }

  private save(userId: string | null, area: LimitArea, value: number | null): void {
    const key = `${userId ?? 'default'}:${area}`;
    this.saving[key] = true;
    this.limits.set(userId, area, value).subscribe({
      next: (o) => {
        this.saving[key] = false;
        this.apply(o);
        this.toastr.success(this.t.instant('pages.settings.aiLimits.saved'));
      },
      error: () => {
        this.saving[key] = false;
        this.toastr.danger(this.t.instant('pages.settings.aiLimits.saveError'));
        this.load();
      },
    });
  }

  trackRow(_: number, row: UserLimitRow): string {
    return row.user_id;
  }
}
